/**
 * ARTWORK STUDIO — the Canva folder map (Master Spec Section 11).
 *
 * Every picker in the studio is FOLDER-SCOPED: it opens on exactly one folder
 * and searches only that folder's subtree. "All of Canva" is never an option —
 * that is the whole point of the paint-by-numbers principle (Section 2).
 *
 * `verified` marks the ids Brian confirmed. Section 12 open confirmation #1 —
 * which shared folder is Laura's presentation/background folder — is NOT
 * settled, so `backgrounds` points at Presentation Templates and says so in the
 * UI rather than pretending it is confirmed. An unconfirmed folder still
 * navigates: the rep can breadcrumb up to the engine root and find it by hand,
 * which is why nothing here ever resolves to a dead picker.
 */

import { DESIGN_LIBRARY_NAMES } from './designLibrary.js'

export const CANVA_ROOT = { id: 'FAHQEPDVjdI', name: 'SR Artwork Engine', verified: true }

export const STUDIO_FOLDERS = {
  root: CANVA_ROOT,
  bezel: { id: 'FAHQEAIMN7g', name: 'Bezel & Top Designs', verified: true },
  shanks: { id: 'FAHQEEVdZ8M', name: 'Side Shanks', verified: true },
  housings: { id: 'FAHQEGgYJoU', name: 'Ring Housings & Templates', verified: true },
  stones: { id: 'FAHQEMRk5Z0', name: 'Stones & Metals', verified: true },
  personalization: { id: 'FAHQEKgfWgQ', name: 'Personalization Templates', verified: true },
  // Section 12, open confirmation #1 — candidates were Presentation Templates
  // (FAHQC_ao03o), Backgrounds (FAHOKhJpLN0) and Artwork Presentation Layouts
  // (FAHQC4cnmEk). Presentation Templates is the one carried here; flagged
  // unconfirmed in the UI until Brian names Laura's folder.
  backgrounds: { id: 'FAHQC_ao03o', name: 'Presentation Templates', verified: false },
  proofs: { id: 'FAHQEGhP1RE', name: 'Proofs & Submissions', verified: true },

  /* THE BEZEL DROP LIBRARY — the second source for the bezel step.
   *
   * ⚠️ IT WAS EMPTY WHEN THIS WAS LAST LOOKED AT, AND IT IS NOT ANY MORE.
   * Measured live 2026-09-09 through the connected Canva account:
   * `01 — Library (the generator pulls from here)` holds ELEVEN sport
   * sub-folders — Football, Baseball and Softball, Basketball, Cheer, Track and
   * Field, Wrestling, Soccer, Volleyball, Golf, Hockey, Multi-Sport and Generic
   * — with real versioned drops inside (Football alone carries V1.1 → V2.3).
   * That is why this is wired as a real picker rather than reported as a gap.
   *
   * `00 — Masters (clean only)` is EMPTY and is deliberately NOT pointed at:
   * a picker onto an empty folder is the thing that teaches a rep the feature
   * is broken. `02 — Exports` is finished per-account work, not a source.
   *
   * A drop IS a bezel. So it writes `components.bezel` like any other bezel
   * design and needs no new payload field — the engine already reads it. */
  bezelDrops: { id: 'FAHThtXA9jI', name: 'Bezel Drops · Library', verified: true },
  bezelDropsRoot: { id: 'FAHThhsan9s', name: 'Bezel Drops', verified: true },
}

/**
 * The two sources the BEZEL step can open on. Same destination
 * (`components.bezel`), two libraries — the curated Bezel & Top set, and the
 * sport-organised drops the Bezel Drop Director works from.
 */
export const BEZEL_SOURCES = [
  { key: 'bezel', label: 'Bezel & Top Designs', note: 'The curated set — 300+ across sport sub-folders.' },
  { key: 'bezelDrops', label: 'Bezel Drops', note: 'The drop library, organised by sport. Versioned drops (V1.1, V2.3…).' },
]

/**
 * All folder names we know, so a breadcrumb has a name before the API answers.
 *
 * The design library's names are folded in for BREADCRUMBS ONLY — naming a
 * folder is not the same as scoping a picker to it, and `folderForStep` below
 * still never returns one. Component ids win a collision because this table is
 * what the element pickers read.
 */
export const FOLDER_NAMES = Object.fromEntries([
  ...Object.entries(DESIGN_LIBRARY_NAMES),
  ...Object.values(STUDIO_FOLDERS).map((f) => [f.id, f.name]),
])

/**
 * Which folder a step's picker opens on. Unknown steps open the engine ROOT so
 * the rep can always navigate by hand — never a dead picker.
 */
export function folderForStep(stepKey = '') {
  const k = String(stepKey).toLowerCase()
  if (/bezel|top/.test(k)) return STUDIO_FOLDERS.bezel
  if (/shank/.test(k)) return STUDIO_FOLDERS.shanks
  if (/housing/.test(k)) return STUDIO_FOLDERS.housings
  if (/stone|girdle|metal/.test(k)) return STUDIO_FOLDERS.stones
  if (/background|presentation/.test(k)) return STUDIO_FOLDERS.backgrounds
  if (/palm|core|engrav|personal|title/.test(k)) return STUDIO_FOLDERS.personalization
  return STUDIO_FOLDERS.root
}
